import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, PoolFactuurRegels } from '../components';
import { ArboBeleidVerklaring } from '../components/ArboBeleidVerklaring';
import { FileText, Check, Calendar, Euro, Eye, CreditCard, ChevronDown, ChevronRight, ShieldCheck, Users, Flag, GraduationCap, Briefcase, Siren, Send, Stethoscope, Zap } from 'lucide-react';
import { fetchEmployerInvoices, fetchEmployerInvoiceDetail } from '../services/invoiceApi';
import type { InvoiceStream } from '../services/invoiceApi';
import api from '../services/api';
import { getSalarisStatus, stuurFactuurNaarSalaris } from '../services/employerTrajectenApi';
import { PROFESSIONAL_CATEGORIES } from '../types/professionalRates';
import toast from 'react-hot-toast';

/**
 * Uiterlijk per facturatiestroom. Een verzamelfactuur ziet er anders uit dan
 * de klassieke factuur per opdracht, en HR moet in één oogopslag zien of het
 * om een anonieme vertrouwenspersoon-factuur gaat — daar valt niets te
 * herleiden en dus ook niets na te vragen.
 */
const STREAM_STIJL: Record<InvoiceStream, { icon: React.ComponentType<{ className?: string }>; klasse: string }> = {
  TRAJECT_BATCH: { icon: Users, klasse: 'bg-blue-100 text-blue-800' },
  ANONYMOUS_VP: { icon: ShieldCheck, klasse: 'bg-amber-100 text-amber-800' },
  PROJECT_MILESTONE: { icon: Flag, klasse: 'bg-purple-100 text-purple-800' },
  ACADEMY: { icon: GraduationCap, klasse: 'bg-emerald-100 text-emerald-800' },
  B2B: { icon: Briefcase, klasse: 'bg-slate-100 text-slate-800' },
  CRISIS: { icon: Siren, klasse: 'bg-red-100 text-red-800' },
  ADHOC: { icon: Zap, klasse: 'bg-teal-100 text-teal-800' },
};

/**
 * Hoofdmenu Trajecten → Facturen: per maand ingedeeld in de vier trajecten
 * van "Lopende Trajecten & Regie". Op de 1e van de maand staan de nieuwe
 * facturen klaar; na goedkeuring gaan ze door naar de salarisadministratie.
 */
type TrajectGroep = 'anoniem' | 'b2b' | 'arbo' | 'calamiteiten' | 'overig';
const TRAJECT_GROEPEN: Array<{ key: TrajectGroep; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'anoniem', label: '1. Anonieme medewerkers', icon: ShieldCheck },
  { key: 'b2b', label: '2. Milestones & interne professionals (B2B)', icon: Briefcase },
  { key: 'arbo', label: '3. Arbodienst', icon: Stethoscope },
  { key: 'calamiteiten', label: '4. Calamiteiten', icon: Siren },
  { key: 'overig', label: 'Overige facturen', icon: FileText },
];
const ARBO_TYPES: string[] = [...PROFESSIONAL_CATEGORIES.arbo.types, 'arbeidsdeskundige'];

function trajectGroep(inv: any): TrajectGroep {
  switch (inv.invoice_type as InvoiceStream | null) {
    case 'CRISIS':
      return 'calamiteiten';
    case 'B2B':
    case 'PROJECT_MILESTONE':
      return 'b2b';
    case 'ANONYMOUS_VP':
    case 'ACADEMY':
    case 'ADHOC':
      return 'anoniem';
    case 'TRAJECT_BATCH':
      return ARBO_TYPES.includes(inv.pool_professional_type) ? 'arbo' : 'anoniem';
    default:
      return 'overig';
  }
}

const maandSleutel = (inv: any) =>
  inv.invoice_year && inv.invoice_month
    ? `${inv.invoice_year}-${String(inv.invoice_month).padStart(2, '0')}`
    : (inv.invoice_date || '').slice(0, 7) || 'onbekend';
const maandLabel = (sleutel: string) =>
  sleutel === 'onbekend' ? 'Onbekende periode' : new Date(`${sleutel}-01T00:00:00`).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });

/** Regels van een poolfactuur; pas opgehaald zodra HR de factuur openklapt. */
const PoolFactuurDetail: React.FC<{ invoiceId: string; stream: InvoiceStream }> = ({ invoiceId, stream }) => {
  const { t } = useTranslation('employer');

  const { data, isLoading, error } = useQuery({
    queryKey: ['employer-invoice-detail', invoiceId],
    queryFn: async () => (await fetchEmployerInvoiceDetail(invoiceId)).data,
  });

  if (isLoading) return <p className="mt-4 text-sm text-gray-500">{t('invoices.loading')}</p>;
  if (error || !data) return <p className="mt-4 text-sm text-red-600">{t('invoices.loadError')}</p>;

  return (
    <PoolFactuurRegels
      stream={stream}
      regels={data.pool_lines || []}
      nettoOmzet={data.professional_payout}
      platformFee={data.platform_fee_amount}
      platformFeePercentage={data.platform_fee_percentage}
      btwBedrag={data.vat_amount}
      totaal={data.total_amount}
      fiatLog={stream === 'PROJECT_MILESTONE' ? data.notes : null}
    />
  );
};

const EmployerInvoicesPage: React.FC = () => {
  const { t } = useTranslation('employer');
  useCockpitTitle(t('invoices.title'), t('invoices.subtitle'));
  const queryClient = useQueryClient();
  const [opengeklapt, setOpengeklapt] = React.useState<string | null>(null);

  // Terugkeer uit Stripe Checkout (?betaling=gelukt|geannuleerd): melding tonen en
  // de lijst verversen; de status komt via de webhook, dus na een paar seconden nog eens.
  const [searchParams, setSearchParams] = useSearchParams();
  const betaling = searchParams.get('betaling');
  React.useEffect(() => {
    if (!betaling) return;
    if (betaling === 'gelukt') toast.success(t('invoices.betalingGelukt', 'Betaling ontvangen. De factuurstatus wordt binnen enkele seconden bijgewerkt.'));
    else if (betaling === 'geannuleerd') toast(t('invoices.betalingGeannuleerd', 'Betaling geannuleerd. De factuur staat nog open.'));
    queryClient.invalidateQueries({ queryKey: ['employer-invoices'] });
    const timer = window.setTimeout(() => queryClient.invalidateQueries({ queryKey: ['employer-invoices'] }), 5000);
    setSearchParams((p) => { p.delete('betaling'); p.delete('session_id'); return p; }, { replace: true });
    return () => window.clearTimeout(timer);
  }, [betaling, queryClient, setSearchParams, t]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['employer-invoices'],
    queryFn: async () => {
      const res = await fetchEmployerInvoices();
      return res.data || [];
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await api.put(`/employer/invoices/${invoiceId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('invoices.approveSuccess'));
      queryClient.invalidateQueries({ queryKey: ['employer-invoices'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('invoices.errorGeneric'));
    },
  });

  const { data: salaris } = useQuery({ queryKey: ['employer-invoices-salaris'], queryFn: getSalarisStatus });
  const salarisMutation = useMutation({
    mutationFn: stuurFactuurNaarSalaris,
    onSuccess: (res) => {
      toast.success(`Doorgestuurd naar salarisadministratie (${res.email})`);
      queryClient.invalidateQueries({ queryKey: ['employer-invoices-salaris'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('invoices.errorGeneric'));
    },
  });

  const payMutation = useMutation({
    mutationFn: async (invoiceId: string) => {
      // Stripe Connect destination charge: werkgever betaalt bruto, Stripe splitst
      // de Belagom-fee aan de bankpoort af naar het platformaccount.
      const response = await api.post(`/employer/invoice/${invoiceId}/pay-stripe`);
      return response.data;
    },
    onSuccess: (data) => {
      const checkoutUrl = data?.data?.checkout_url;
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      }
      queryClient.invalidateQueries({ queryKey: ['employer-invoices'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('invoices.errorGeneric'));
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('invoices.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{t('invoices.loadError')}</p>
        </div>
      </div>
    );
  }

  const invoices = data || [];
  const perMaand = new Map<string, any[]>();
  for (const inv of invoices) {
    const sleutel = maandSleutel(inv);
    perMaand.set(sleutel, [...(perMaand.get(sleutel) ?? []), inv]);
  }
  const maanden = Array.from(perMaand.entries()).sort((a, b) => b[0].localeCompare(a[0]));

  // Goedkeuring staat naast de betaalstatus (employer_approved_at); 'betaald' gaat voor.
  const isGoedgekeurd = (inv: any) => !!inv.employer_approved_at && inv.status !== 'paid';
  const invoiceStatusLabel = (inv: any) => {
    if (isGoedgekeurd(inv)) return t('invoices.statusApproved');
    if (inv.status === 'pending' || inv.status === 'draft') return t('invoices.statusPending');
    if (inv.status === 'sent') return t('invoices.statusSent');
    if (inv.status === 'overdue') return t('invoices.statusOverdue');
    if (inv.status === 'paid') return t('invoices.statusPaid');
    if (inv.status === 'rejected') return t('invoices.statusRejected');
    return inv.status;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ArboBeleidVerklaring />

      {invoices.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('invoices.empty')}
            </h2>
            <p className="text-gray-600">
              {t('invoices.emptyDesc')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-8">
          {maanden.map(([sleutel, lijst]) => {
            const open = lijst.filter((i: any) => i.status === 'pending' || i.status === 'draft').length;
            return (
              <section key={sleutel}>
                <div className="flex flex-wrap items-baseline gap-3 mb-3 border-b border-gray-200 pb-2">
                  <h2 className="text-lg font-semibold text-gray-900 capitalize">{maandLabel(sleutel)}</h2>
                  {open > 0 && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">{open} goed te keuren</span>}
                </div>
                {TRAJECT_GROEPEN.map((groep) => {
                  const facturen = lijst.filter((i: any) => trajectGroep(i) === groep.key);
                  if (!facturen.length) return null;
                  const Icoon = groep.icon;
                  return (
                    <div key={groep.key} className="mb-5">
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <Icoon className="w-4 h-4" />
                        {groep.label}
                      </h3>
                      <div className="space-y-3">{facturen.map(renderFactuur)}</div>
                    </div>
                  );
                })}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );

  function renderFactuur(inv: any) {
    const doorgestuurd = salaris?.doorzendingen?.[inv.id];
    return (
            <Card key={inv.id} className="hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900">{inv.invoice_number}</h3>
                      {inv.invoice_type && (() => {
                        const stijl = STREAM_STIJL[inv.invoice_type as InvoiceStream];
                        const Icoon = stijl?.icon ?? Users;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stijl?.klasse ?? 'bg-gray-100 text-gray-800'}`}>
                            <Icoon className="w-3 h-3" />
                            {t(`invoices.stream.${inv.invoice_type}`)}
                          </span>
                        );
                      })()}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      {inv.invoice_type
                        ? t('invoices.poolSubtitle', { pool: inv.pool_label || inv.pool_professional_type })
                        : inv.opdracht_title || t('invoices.noTitle')}
                    </p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('nl-NL') : t('invoices.noDate')}
                      </div>
                      <div className="flex items-center">
                        <Euro className="w-4 h-4 mr-1" />
                        {inv.total_amount ? (typeof inv.total_amount === 'string' ? parseFloat(inv.total_amount).toFixed(2) : inv.total_amount.toFixed(2)) : '0.00'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isGoedgekeurd(inv) || inv.status === 'paid'
                        ? 'bg-green-100 text-green-800'
                        : inv.status === 'overdue'
                        ? 'bg-red-100 text-red-800'
                        : inv.status === 'pending' || inv.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-800'
                        : inv.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {invoiceStatusLabel(inv)}
                    </span>
                    <div className="flex items-center gap-2">
                      {inv.invoice_type && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOpengeklapt(opengeklapt === inv.id ? null : inv.id)}
                        >
                          {opengeklapt === inv.id ? (
                            <ChevronDown className="w-4 h-4 mr-1" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-1" />
                          )}
                          {t('invoices.showLines')}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Add timestamp to prevent caching
                          const timestamp = new Date().getTime();
                          const url = `/api/invoices/employer/${inv.id}/pdf?t=${timestamp}`;
                          // Open in new window - browser will handle PDF display
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('invoices.viewPdf')}
                      </Button>
                      {!inv.employer_approved_at && ['pending', 'draft', 'sent', 'overdue'].includes(inv.status) && (
                        <Button
                          size="sm"
                          onClick={() => approveMutation.mutate(inv.id)}
                          loading={approveMutation.isPending}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t('invoices.approve')}
                        </Button>
                      )}
                      {(inv.employer_approved_at || inv.status === 'paid') && (
                        <Button
                          size="sm"
                          variant="outline"
                          title={doorgestuurd ? `Doorgestuurd op ${new Date(doorgestuurd.verzonden_at).toLocaleDateString('nl-NL')} naar ${doorgestuurd.email}` : salaris?.salaris_email ?? undefined}
                          onClick={() => salarisMutation.mutate(inv.id)}
                          loading={salarisMutation.isPending && salarisMutation.variables === inv.id}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {doorgestuurd ? 'Opnieuw naar salarisadministratie' : 'Naar salarisadministratie'}
                        </Button>
                      )}
                      {inv.billing_model === 'self_billing' && inv.status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => payMutation.mutate(inv.id)}
                          loading={payMutation.isPending}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          {t('invoices.payNow')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {doorgestuurd && (
                  <p className="mt-2 text-xs text-green-700">
                    Doorgestuurd naar salarisadministratie ({doorgestuurd.email}) op {new Date(doorgestuurd.verzonden_at).toLocaleDateString('nl-NL')}
                  </p>
                )}
                {inv.invoice_type && opengeklapt === inv.id && (
                  <PoolFactuurDetail invoiceId={inv.id} stream={inv.invoice_type as InvoiceStream} />
                )}
              </div>
            </Card>
    );
  }
};

export default EmployerInvoicesPage;
